import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@auth0/auth0-spa-js';
import { Bell, History, LogOut, MessageSquare, Pencil, SquarePen } from 'lucide-react';
import { useChat } from '../chat/useChat';
import { useSendMessage } from '../chat/useSendMessage';
import { useNotifications } from '../notifications/useNotifications';
import { NotificationPanel } from '../notifications/NotificationPanel';
import { ToastHost } from '../components/Toast/ToastHost';
import { featureToTarget } from '../notifications/featureMap';
import type { NotificationItem } from '../notifications/types';
import { TaskPaneChatInput, type ChatInputHandle } from '../chat/TaskPaneChatInput';
import { TaskPaneMessageCard } from '../chat/TaskPaneMessageCard';
import { ChatHistorySidebar } from '../chat/ChatHistorySidebar';
import { ModeSwitchConfirmModal } from '../chat/ModeSwitchConfirmModal';
import { LinkDraftPicker } from '../chat/LinkDraftPicker';
import { OpenDraftInNewWindowModal } from '../chat/OpenDraftInNewWindowModal';
import { OutdatedVersionBanner } from '../chat/OutdatedVersionBanner';
import { AskToLinkBanner } from '../chat/AskToLinkBanner';
import { PinAddInTooltip } from '../chat/PinAddInTooltip';
import { BannerStack } from '../chat/BannerStack';
import { readDraftProvenance, stampDraftProvenance } from '../office/provenance';
import {
  forkChat,
  fetchChatList,
  fetchChatMessages,
  fetchLatestDocxVersion,
  updateChatMeta,
  type ChatThreadSummary,
} from '../api/chatHistory';
import { downloadDocxFromS3 } from '../api/s3';
import { applyEditedDocument } from '../office/ooxmlRedline';
import { getWordEditState, pushSnapshot, setWordEditState } from '../office/documentSettings';
import {
  NewWindowOpenError,
  openDocxInNewWordWindow,
  takePendingDraftLink,
  writePendingDraftLink,
} from '../office/dialog';
import { bytesToBase64 } from '../lib/utils';
import { readCurrentDocumentBytes, computeDocumentHash } from '../office/documentIO';
import type { ChatMessage } from '../chat/types';
import { ResearchStatusPanel } from '../components/ResearchStatusPanel/ResearchStatusPanel';
import { CitationPanel } from '../components/CitationPanel/CitationPanel';
import { ScrollArea } from '../components/atoms/ScrollArea';
import { Button } from '../components/atoms/Button';
import { useAuth } from '../auth/AuthContext';
import { getSelectedText } from '../office/documentIO';
import { cn } from '../lib/utils';
import { FEATURES } from '../config/features';
import { useDraft } from '../chat/draft/useDraft';
import {
  DraftLoadingOverlay,
  DraftPromptHint,
  DraftQnaOverlay,
  DraftTemplateOverlay,
} from '../chat/draft/DraftOverlays';
import { DraftMethodPicker } from '../chat/draft/DraftMethodPicker';
import { DraftQualityToggle } from '../chat/draft/DraftQualityToggle';
import { InlineTitleEditor } from '../components/atoms/InlineTitleEditor';
import { ThinkingPill } from '../components/loaders/ThinkingPill';
import {
  EDIT_THINKING_MESSAGES,
  RESEARCH_THINKING_MESSAGES,
} from '../chat/draft/draftLoadingMessages';
import type { Citation } from '../chat/types';

/**
 * Main task pane after login. Owns:
 *   - Chat state via useChat + shared SSE subscription
 *   - Send pipeline via useSendMessage (handles full_edit + quote_edit applies)
 *   - History sidebar + citation panel overlay state
 *   - Header with user name + new-chat + logout
 */
export function TaskPane({ user }: { user: User }): JSX.Element {
  const { logout } = useAuth();
  const chat = useChat();
  const send = useSendMessage({ user, mode: chat.mode, chat });
  const notifications = useNotifications();

  // Open a notification's target chat thread in the pane. Mirrors the history
  // sidebar's select flow (fetch messages → loadThread). Notifications whose
  // feature has no chat thread (doc-processing, etc.) just get marked read.
  const handleOpenNotification = useCallback(
    async (item: NotificationItem) => {
      void notifications.markRead(item.id);
      notifications.closePanel();
      const target = featureToTarget(item.feature, item.featureId);
      if (target.kind === 'none') return;
      try {
        const msgs = await fetchChatMessages(target.chatId);
        chat.loadThread(target.chatId, msgs, target.chatType);
        chat.setMode(target.mode);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[notifications] open-in-pane failed', err);
      }
    },
    [chat, notifications],
  );
  // Draft state machine. The hook is cheap when unused — it subscribes to SSE
  // but its handler short-circuits unless `type === 'draft'` events arrive.
  // We still gate all UI on `FEATURES.draftMode` below.
  const draft = useDraft();

  const [selectionPreview, setSelectionPreview] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeCitation, setActiveCitation] = useState<Citation | null>(null);
  const [paneWidth, setPaneWidth] = useState(0);
  // Pending mode-switch state — when a send hits a chat_type mismatch, we
  // stash the text until the user confirms the fork, then replay the send
  // into the newly-forked chat.
  const [pendingForkText, setPendingForkText] = useState<string | null>(null);
  const [forkBusy, setForkBusy] = useState(false);
  // Link-draft picker state. When `null` the picker is closed. Also reflects
  // the current linked-draft name for the header badge.
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkedDraftName, setLinkedDraftName] = useState<string | null>(() => {
    try {
      return getWordEditState().linkedDraftName ?? null;
    } catch {
      return null;
    }
  });
  // Draft-switcher modal state — set when the user clicks a draft in History
  // that isn't the currently-open document.
  const [switcherTarget, setSwitcherTarget] = useState<{
    thread: ChatThreadSummary;
    messages: ChatMessage[];
  } | null>(null);
  const [switcherBusy, setSwitcherBusy] = useState(false);
  // Phase E — outdated-copy banner state.
  const [outdatedInfo, setOutdatedInfo] = useState<{
    s3Key: string;
    version: string | null;
    sha256: string | null;
  } | null>(null);
  const [reloadBusy, setReloadBusy] = useState(false);
  // Phase F — ask-to-link banner state. Populated when a provenance stamp
  // is detected on mount AND the document isn't currently linked.
  const [detectedProvenance, setDetectedProvenance] = useState<{
    chatId: string;
    name: string | null;
  } | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  // Inline error banner — Office WebView disables window.alert(), so we
  // surface user-facing errors via a small dismissable banner instead.
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  // One-time "pin the add-in" tooltip after the first successful
  // new-window switcher open. localStorage-backed so it never nags again.
  const [pinHintVisible, setPinHintVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Input text lives inside TaskPaneChatInput (its own useState). We only
  // reach in via this ref when we need to imperatively clear (e.g. on new-chat).
  const chatInputRef = useRef<ChatInputHandle>(null);

  // On mount: if a different plugin window just launched THIS window via
  // ms-word:ofe|u|, pick up the handshake from localStorage and auto-link
  // this fresh document to the target draft chat. Also loads the chat
  // messages into the pane so the user sees conversation context immediately.
  useEffect(() => {
    const pending = takePendingDraftLink();
    if (!pending?.chatId) return;
    let cancelled = false;
    (async () => {
      try {
        await setWordEditState({
          linkedDraftChatId: pending.chatId,
          linkedDraftName: pending.name ?? null,
          chatId: null,
        });
        if (cancelled) return;
        setLinkedDraftName(pending.name ?? null);
        const msgs = await fetchChatMessages(pending.chatId);
        if (cancelled) return;
        chat.loadThread(pending.chatId, msgs, 'draft');
        chat.setMode('edit');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[TaskPane] pending draft link claim failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally empty deps — only runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase F — provenance detection + ask-to-link.
  // Runs once on mount. If the document's OOXML customXml parts contain a
  // NyayAssist stamp AND the document isn't already linked AND the user
  // hasn't previously declined this specific chat_id, surface the banner.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prov = await readDraftProvenance();
        if (cancelled || !prov?.chatId) return;
        const settings = getWordEditState();
        if (settings.linkedDraftChatId === prov.chatId) return; // already linked to it
        if (settings.declinedAutoLinkChatIds?.includes(prov.chatId)) return; // previously declined
        // Try to fetch a friendly draft name for the banner copy.
        let name: string | null = null;
        try {
          const drafts = await fetchChatList('draft', { limit: 50 });
          name = drafts.find((d) => d.chatId === prov.chatId)?.name ?? null;
        } catch {
          /* name is optional — banner still works without it */
        }
        if (!cancelled) {
          setDetectedProvenance({ chatId: prov.chatId, name });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[TaskPane] provenance detection failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Task-pane width drives the citation layout: above ~720px we render chat +
  // citation side-by-side (web UI style); below that, citation overlays the chat.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    setPaneWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setPaneWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Threshold: below ~560px side-by-side is too cramped for either column to
  // be useful. Above that, we give chat ~40% and PDF ~60% (min chat 280px).
  const splitMode = !!activeCitation && paneWidth >= 560;
  const overlayMode = !!activeCitation && !splitMode;

  // Auto-scroll to bottom on new messages / status updates.
  //
  // Previously this ran a sync `scrollHeight` + `scrollTop` assignment on
  // EVERY SSE chunk (which triggers a layout/reflow each time and stalls
  // the streaming UI on long threads). The dependency array sees a new
  // `chat.messages` array reference on every `updateMessage` call, so for
  // a streaming research answer with N partial chunks we paid the layout
  // cost N times.
  //
  // Now we batch via requestAnimationFrame: any number of message updates
  // within a single frame collapse into one scroll. Skips runs while the
  // user has scrolled UP (so we don't fight them mid-thread).
  const scrollRafRef = useRef<number | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Only auto-scroll if the user is already near the bottom — don't
    // hijack scroll position when they're reading prior messages.
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (!nearBottom) return;

    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });

    return () => {
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [chat.messages, chat.researchStatus]);

  // Snapshot current Word selection on mode switch to show a context chip
  useEffect(() => {
    let cancelled = false;
    getSelectedText()
      .then((s) => {
        if (!cancelled && s && s.length > 4) setSelectionPreview(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chat.mode]);

  const dispatchSend = useCallback(
    (text: string) => {
      if (FEATURES.draftMode && chat.mode === 'draft') {
        const userId = user.sub ?? 'unknown';
        // If we already have a loaded draft chat (fork / history resume),
        // reuse its chat_id — otherwise start fresh.
        const shouldAttachExisting =
          !!chat.currentThreadId &&
          chat.loadedChatType === 'draft' &&
          draft.chatId !== chat.currentThreadId;
        // `alreadyExists` flag mirrors web's gate: only call POST /chat when
        // the thread is brand new. Any history-resumed / forked chat is
        // already server-side.
        const alreadyExists =
          shouldAttachExisting || (chat.loadedChatType === 'draft' && !!chat.currentThreadId);
        const prep = shouldAttachExisting
          ? draft.attach(chat.currentThreadId as string)
          : draft.chatId
            ? Promise.resolve()
            : draft.start();
        void prep.then(() => draft.sendPrompt(text, userId, alreadyExists));
        return;
      }
      void send.send(text);
    },
    [chat.mode, chat.currentThreadId, chat.loadedChatType, draft, send, user.sub],
  );

  const onSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Mode-switch fork guard — only fork when the current thread's
      // chat_type is actually incompatible with the selected mode.
      // Edit mode on a draft chat is intentionally NOT a fork (follow-up
      // edits stay in the same draft so the web draft sidebar sees them).
      if (
        chat.currentThreadId &&
        chat.loadedChatType &&
        needsFork(chat.loadedChatType, chat.mode)
      ) {
        setPendingForkText(trimmed);
        return;
      }
      dispatchSend(trimmed);
    },
    [chat.currentThreadId, chat.loadedChatType, chat.mode, dispatchSend],
  );

  const confirmFork = useCallback(async () => {
    const text = pendingForkText;
    const sourceId = chat.currentThreadId;
    const nextType = newChatTypeForMode(chat.mode);
    if (!text || !sourceId) {
      setPendingForkText(null);
      return;
    }
    setForkBusy(true);
    try {
      const { chatId: newChatId } = await forkChat({
        sourceChatId: sourceId,
        newChatType: nextType,
      });
      const hydrated = await fetchChatMessages(newChatId);
      chat.loadThread(newChatId, hydrated, nextType);
      // Replay the original send into the new chat.
      dispatchSend(text);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[TaskPane] fork failed', err);
      setErrorBanner('Could not start the new chat. Please try again.');
    } finally {
      setForkBusy(false);
      setPendingForkText(null);
    }
  }, [chat, chat.mode, dispatchSend, pendingForkText]);

  const cancelFork = useCallback(() => {
    setPendingForkText(null);
  }, []);

  const handleLinkDraft = useCallback(
    async (thread: { chatId: string; name?: string | null }) => {
      await setWordEditState({
        linkedDraftChatId: thread.chatId,
        linkedDraftName: thread.name ?? null,
        // Clear chatId so the next send picks up the linked chat.
        chatId: null,
      });
      setLinkedDraftName(thread.name ?? null);
      setLinkPickerOpen(false);
    },
    [],
  );

  /**
   * Phase E — outdated-copy check. Runs whenever the linked-draft chatId
   * changes. Pulls the latest metadata from the chat's messages and
   * compares against what we last applied locally. If either the version
   * or the SHA differs — and the user hasn't already dismissed this exact
   * update — surface the banner.
   */
  const checkOutdatedCopy = useCallback(async () => {
    const state = getWordEditState();
    const chatId = state.linkedDraftChatId;
    if (!chatId) {
      setOutdatedInfo(null);
      return;
    }
    try {
      const latest = await fetchLatestDocxVersion(chatId);
      if (!latest.s3Key) return;
      const localVersion = state.lastAppliedVersion ?? null;
      const localSha = state.lastAppliedSha256 ?? null;
      const serverVersion = latest.version;
      const serverSha = latest.sha256;

      // Treat as outdated if server has a different s3_key/sha, OR the
      // version numerically jumped ahead.
      const shaMismatch = !!serverSha && !!localSha && serverSha !== localSha;
      const versionAhead =
        !!serverVersion &&
        !!localVersion &&
        parseInt(serverVersion, 10) > parseInt(localVersion, 10);
      const noLocalState = !localVersion && !localSha;

      if (!shaMismatch && !versionAhead && !noLocalState) {
        setOutdatedInfo(null);
        return;
      }

      const key = `${serverVersion ?? ''}:${serverSha ?? ''}`;
      if (state.dismissedUpdateKeys?.includes(key)) {
        setOutdatedInfo(null);
        return;
      }
      setOutdatedInfo({ s3Key: latest.s3Key, version: serverVersion, sha256: serverSha });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[TaskPane] outdated-copy check failed', err);
    }
  }, []);

  const dismissOutdatedBanner = useCallback(async () => {
    if (!outdatedInfo) return;
    const state = getWordEditState();
    const key = `${outdatedInfo.version ?? ''}:${outdatedInfo.sha256 ?? ''}`;
    const next = Array.from(new Set([...(state.dismissedUpdateKeys ?? []), key]));
    await setWordEditState({ dismissedUpdateKeys: next });
    setOutdatedInfo(null);
  }, [outdatedInfo]);

  const reloadLatestVersion = useCallback(async () => {
    if (!outdatedInfo) return;
    setReloadBusy(true);
    try {
      const buf = await downloadDocxFromS3(outdatedInfo.s3Key);
      const bytes = new Uint8Array(buf);
      await applyEditedDocument(bytes, undefined);
      await setWordEditState({
        currentS3Key: outdatedInfo.s3Key,
        lastAppliedVersion: outdatedInfo.version,
        lastAppliedSha256: outdatedInfo.sha256,
        dismissedUpdateKeys: [],
      });
      setOutdatedInfo(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[TaskPane] reload latest failed', err);
      setErrorBanner('Could not reload the latest version.');
    } finally {
      setReloadBusy(false);
    }
  }, [outdatedInfo]);

  const confirmAutoLink = useCallback(async () => {
    if (!detectedProvenance) return;
    setLinkBusy(true);
    try {
      await setWordEditState({
        linkedDraftChatId: detectedProvenance.chatId,
        linkedDraftName: detectedProvenance.name,
        chatId: null,
      });
      setLinkedDraftName(detectedProvenance.name);
      const msgs = await fetchChatMessages(detectedProvenance.chatId);
      chat.loadThread(detectedProvenance.chatId, msgs, 'draft');
      chat.setMode('edit');
      setDetectedProvenance(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[TaskPane] confirmAutoLink failed', err);
    } finally {
      setLinkBusy(false);
    }
  }, [chat, detectedProvenance]);

  const declineAutoLink = useCallback(async () => {
    if (!detectedProvenance) return;
    const state = getWordEditState();
    const next = Array.from(
      new Set([...(state.declinedAutoLinkChatIds ?? []), detectedProvenance.chatId]),
    );
    await setWordEditState({ declinedAutoLinkChatIds: next });
    setDetectedProvenance(null);
  }, [detectedProvenance]);

  // On mount + whenever the linked draft changes + on window focus, check
  // whether the server has a newer version of this doc than what we
  // applied locally. Surfaces the OutdatedVersionBanner when true.
  useEffect(() => {
    void checkOutdatedCopy();
    const onFocus = () => void checkOutdatedCopy();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [checkOutdatedCopy, linkedDraftName]);

  const handleUnlinkDraft = useCallback(async () => {
    await setWordEditState({
      linkedDraftChatId: null,
      linkedDraftName: null,
      chatId: null,
    });
    setLinkedDraftName(null);
  }, []);

  // ----- Draft-switcher (new Word window) handlers -----

  /**
   * Resolve the latest DOCX's s3_key for a chat. Looks at three sources
   * in order:
   *   1. thread.metadata (chat row metadata). fetchChatList runs
   *      convertKeysToCamelCase recursively, so we read `s3Key` (camel),
   *      but accept `s3_key` (snake) as a forward-compat fallback.
   *   2. Any message in `messages` with editedS3Key set (history).
   *   3. fetchLatestDocxVersion(chatId) — round-trip fallback walking
   *      message metadata directly (snake_case, not camel-converted).
   */
  const resolveDraftS3Key = useCallback(
    async (
      thread: ChatThreadSummary,
      messages: ChatMessage[],
    ): Promise<string | null> => {
      // 1. chat row metadata (keys are camelCase after convertKeysToCamelCase)
      const threadMeta = thread.metadata as
        | { s3Key?: string; s3_key?: string }
        | undefined;
      const fromChatRow =
        (typeof threadMeta?.s3Key === 'string' && threadMeta.s3Key) ||
        (typeof threadMeta?.s3_key === 'string' && threadMeta.s3_key) ||
        '';
      if (fromChatRow) {
        // eslint-disable-next-line no-console
        console.info('[Switcher] s3_key from thread.metadata');
        return fromChatRow;
      }
      // 2. message history
      for (let i = messages.length - 1; i >= 0; i--) {
        const key = messages[i].editedS3Key;
        if (key) {
          // eslint-disable-next-line no-console
          console.info('[Switcher] s3_key from message history');
          return key;
        }
      }
      // 3. refetch — in case the message metadata shape is off
      try {
        const latest = await fetchLatestDocxVersion(thread.chatId);
        if (latest.s3Key) {
          // eslint-disable-next-line no-console
          console.info('[Switcher] s3_key from fetchLatestDocxVersion');
          return latest.s3Key;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Switcher] fetchLatestDocxVersion failed', err);
      }
      return null;
    },
    [],
  );

  /**
   * Launch a selected draft in Word.
   *
   * Two modes:
   *   - 'new-window'   → opens in a fresh Word window via Word.Application
   *                      .openDocument (WordApi 1.6) or createDocument(base64)
   *                      .open() (WordApi 1.3). Current document untouched.
   *   - 'replace'      → replaces the current document's content in place.
   *                      Pushes a snapshot so the Revert action can restore.
   *
   * `new-window` is the preferred UX; `replace` is the explicit fallback
   * (also auto-used if both new-window routes fail on an older Word build).
   */
  const launchSwitcher = useCallback(
    async (opts: { mode: 'new-window' | 'replace' }) => {
      if (!switcherTarget) return;
      const { thread, messages } = switcherTarget;
      // eslint-disable-next-line no-console
      console.info('[Switcher] request', {
        mode: opts.mode,
        chatId: thread.chatId,
        name: thread.name,
      });
      setSwitcherBusy(true);
      try {
        const s3Key = await resolveDraftS3Key(thread, messages);
        if (!s3Key) {
          // eslint-disable-next-line no-console
          console.warn('[Switcher] no s3_key found for', thread.chatId);
          setErrorBanner(
            'This draft has no saved document version yet. Generate it in the web app first.',
          );
          return;
        }

        if (opts.mode === 'new-window') {
          // Always fetch the bytes — we open in a new window via
          // createDocument(base64).open(), never via URL. Bypasses Word's
          // URL-parser trust prompt and the "Sorry, we couldn't open …"
          // error that happens when S3 presigned URLs contain `|` in the
          // path (our `google-oauth2|<sub>` key convention).
          let base64: string;
          try {
            const buf = await downloadDocxFromS3(s3Key);
            base64 = bytesToBase64(new Uint8Array(buf));
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[Switcher] bytes fetch failed', err);
            setErrorBanner(
              'Could not download the draft. Please try again in a moment.',
            );
            return;
          }
          // Handshake for the next plugin instance so it auto-links to
          // the target chat when its taskpane mounts (even if the DOCX
          // doesn't carry our provenance stamp yet).
          writePendingDraftLink({
            chatId: thread.chatId,
            draftId: thread.chatId,
            name: thread.name ?? '',
          });
          try {
            await openDocxInNewWordWindow({ base64Bytes: base64 });
            // eslint-disable-next-line no-console
            console.info('[Switcher] new-window succeeded');
            // First-time hint: if the user hasn't dismissed before, tell them
            // to pin the add-in so it auto-opens in the new window. We can't
            // force it programmatically — Office controls per-window taskpane.
            try {
              if (!window.localStorage.getItem('nyayassist:pinHintShown')) {
                setPinHintVisible(true);
              }
            } catch {
              /* storage disabled — skip the hint */
            }
            return;
          } catch (err) {
            if (err instanceof NewWindowOpenError) {
              // eslint-disable-next-line no-console
              console.warn('[Switcher] new-window failed, falling back to replace', err);
              setErrorBanner(
                'Your Word build is too old to open a new window from a task pane. Falling back to in-place load — Revert from the header if you want to go back.',
              );
              // Fall through to the replace path below using the bytes
              // we already downloaded.
            } else {
              throw err;
            }
          }
          await replaceInPlace({
            thread,
            messages,
            s3Key,
            preDownloadedBase64: base64,
          });
          return;
        }

        // Explicit replace-in-place path.
        await replaceInPlace({ thread, messages, s3Key, preDownloadedBase64: null });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Switcher] launch failed', err);
        setErrorBanner(
          err instanceof Error
            ? `Could not load this draft: ${err.message}`
            : 'Could not load this draft. Please try again.',
        );
      } finally {
        setSwitcherBusy(false);
        setSwitcherTarget(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chat, resolveDraftS3Key, switcherTarget],
  );

  /**
   * Actually replace the current Word document's body with the draft DOCX
   * and rewire plugin state to the new chat. Extracted so both the explicit
   * "Load here" button and the new-window-failed fallback can reuse it.
   */
  const replaceInPlace = useCallback(
    async (args: {
      thread: ChatThreadSummary;
      messages: ChatMessage[];
      s3Key: string;
      preDownloadedBase64: string | null;
    }) => {
      const { thread, messages, s3Key, preDownloadedBase64 } = args;
      // Snapshot for Revert.
      try {
        const bytes = await readCurrentDocumentBytes();
        await pushSnapshot(bytes);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Switcher] snapshot failed (continuing)', err);
      }

      // Download bytes if we don't already have them from the new-window try.
      const buf = preDownloadedBase64
        ? null
        : await downloadDocxFromS3(s3Key);
      const newBytes = buf ? new Uint8Array(buf) : undefined;
      if (newBytes) {
        await applyEditedDocument(newBytes, undefined);
      } else if (preDownloadedBase64) {
        // Decode base64 → bytes for applyEditedDocument.
        const binary = atob(preDownloadedBase64);
        const arr = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
        await applyEditedDocument(arr, undefined);
      }
      // eslint-disable-next-line no-console
      console.info('[Switcher] replaced current document with draft bytes');

      await setWordEditState({
        linkedDraftChatId: thread.chatId,
        linkedDraftName: thread.name ?? null,
        chatId: null,
        currentS3Key: s3Key,
        lastUploadHash: await computeDocumentHash(),
        lastAppliedVersion: null,
        lastAppliedSha256: null,
        dismissedUpdateKeys: [],
      });
      setLinkedDraftName(thread.name ?? null);

      chat.loadThread(thread.chatId, messages, 'draft');
      chat.setMode('edit');

      void stampDraftProvenance({
        chatId: thread.chatId,
        draftId: thread.chatId,
        version: '1',
      });
    },
    [chat],
  );

  const onNewChat = useCallback(() => {
    chat.reset();
    void draft.reset();
    chatInputRef.current?.clear();
    setSelectionPreview(null);
    setActiveCitation(null);
    setHistoryOpen(false);
  }, [chat, draft]);

  // Refs used inside the memoised citation-click handler so its identity
  // stays stable even as messages mutate (keeps message-card memoisation alive).
  const messagesRef = useRef(chat.messages);
  messagesRef.current = chat.messages;

  const onCitationClick = useCallback((citationId: string) => {
    const msgs = messagesRef.current;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const c = msgs[i].citations?.find((x) => x.id === citationId);
      if (c) {
        setActiveCitation(c);
        return;
      }
    }
  }, []);

  // Stable identity so <TaskPaneMessageCard> memoisation isn't defeated on
  // every keystroke. (`send.insertAnswerIntoDocument` is itself memoised by
  // useSendMessage, so the only volatile leg here is the wrapping arrow fn.)
  const onMessageInsert = useCallback(
    (html: string) => {
      void send.insertAnswerIntoDocument(html);
    },
    [send],
  );

  const handleCloseCitation = useCallback(() => setActiveCitation(null), []);
  const handleInsertCitation = useCallback(
    (html: string) => {
      void send.insertAnswerIntoDocument(html);
      setActiveCitation(null);
    },
    [send],
  );

  const hasMessages = chat.messages.length > 0;
  const firstName = useMemo(
    () => (user.given_name as string | undefined) ?? extractFirstName(user.name),
    [user.given_name, user.name],
  );

  const chatColumn = (
    <div className="flex flex-col min-w-0 flex-1 h-full">
      {/* min-h-0 is load-bearing: without it this flex-1 child defaults to
          min-height:auto and refuses to shrink below its content, so a long
          prompt (or tall draft toggles) pushes the input footer past the
          fixed-height pane and clips the Send button. Mirrors CitationPanel. */}
      <ScrollArea
        className="px-3 flex-1 py-4 min-h-0"
        {...{ ref: scrollRef }}
      >
        {!hasMessages ? <EmptyState mode={chat.mode} /> : null}
        {chat.messages.map((m) => (
          <TaskPaneMessageCard
            key={m.id}
            message={m}
            onCitationClick={onCitationClick}
            onInsertIntoDocument={onMessageInsert}
          />
        ))}
        {chat.researchStatus && <ResearchStatusPanel status={chat.researchStatus} />}
        {/*
          Research thinking pill — shown while SSE is streaming a research
          reply but no final answer message has arrived yet. Sits below the
          status panel (which carries the live tool-call trace) and above
          the empty space where the answer will land.
          SOURCE: frontend-prod/features/research/components/ResearchThinkingLoader.tsx
        */}
        {chat.mode === 'research' && !!chat.streamingId && (
          <div className="mb-3">
            <ThinkingPill messages={RESEARCH_THINKING_MESSAGES} />
          </div>
        )}
        {send.editStage !== 'idle' && (
          <div className="mb-3">
            {/* Show the specific stage label when we have one; otherwise rotate
                through the generic edit messages. Either way the pulsing-logo
                animation runs (frontend-prod-aligned). */}
            <ThinkingPill
              messages={
                editStageLabel(send.editStage)
                  ? [editStageLabel(send.editStage)]
                  : EDIT_THINKING_MESSAGES
              }
            />
          </div>
        )}
      </ScrollArea>

      {/* F1 — Auto/Instant draft mode toggle. Shown only when the user is
          about to start a draft (no in-flight phase, no template yet). */}
      {FEATURES.draftMode &&
        chat.mode === 'draft' &&
        (draft.phase === 'idle' || draft.phase === 'prompt') && (
          // shrink-0 keeps the toggles at natural height so they're never
          // squeezed/pushed off when the input grows — only the ScrollArea
          // above absorbs the height pressure.
          <div className="shrink-0">
            <DraftMethodPicker
              value={draft.draftExperience}
              onChange={draft.setDraftExperience}
              disabled={send.editStage !== 'idle'}
            />
            {/* F3 — Quality (Concise/Detailed). Detailed is disabled when
                Auto/Instant is active; useDraft also enforces the clamp. */}
            <DraftQualityToggle
              value={draft.processingMode}
              onChange={draft.setProcessingMode}
              disabled={send.editStage !== 'idle'}
              disabledDetailed={draft.draftExperience === 'instant'}
            />
          </div>
        )}

      <TaskPaneChatInput
        ref={chatInputRef}
        onSend={onSend}
        onStop={chat.stop}
        disabled={send.editStage !== 'idle'}
        mode={chat.mode}
        onModeChange={chat.setMode}
        isStreaming={!!chat.streamingId}
        contextPreview={selectionPreview ?? undefined}
        onClearContext={() => setSelectionPreview(null)}
      />
    </div>
  );

  return (
    <div ref={rootRef} className="relative flex flex-col h-full bg-background">
      <Header
        userName={firstName}
        userPicture={user.picture}
        onLogout={logout}
        onNewChat={onNewChat}
        onToggleHistory={() => setHistoryOpen((x) => !x)}
        onToggleLinkDraft={() => setLinkPickerOpen(true)}
        showLinkDraft={chat.mode === 'edit'}
        editStage={send.editStage}
      />

      {/*
        Top-of-pane banner stack. Renders banners in normal flow as a
        vertical column between the Header and the chat-citation row, so
        they stack naturally instead of competing for the same absolute
        `top-12` slot. Order (top→bottom): error → outdated-version →
        linked-draft/not-linked → ask-to-link → pin-add-in tooltip.
        ChatHistorySidebar (rendered below) covers the whole pane via
        `absolute inset-0 z-30` when open, so banners are implicitly
        hidden while History is up.
      */}
      <BannerStack>
        {errorBanner && !linkPickerOpen && !pinHintVisible && (
          <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border-b border-red-200 text-[11px] text-red-900">
            <span className="flex-1 min-w-0 leading-snug">{errorBanner}</span>
            <button
              type="button"
              onClick={() => setErrorBanner(null)}
              className="text-red-700 hover:text-red-900 text-[10px] font-medium"
              title="Dismiss"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Outdated-copy banner — shown when a newer version of the linked
            draft exists on the server. Phase E. Preempted by ask-to-link
            (handled by detectedProvenance guard above + below). */}
        {outdatedInfo && !linkPickerOpen && !detectedProvenance && (
          <OutdatedVersionBanner
            latestVersion={outdatedInfo.version}
            busy={reloadBusy}
            onReload={() => void reloadLatestVersion()}
            onDismiss={() => void dismissOutdatedBanner()}
          />
        )}

        {/* Linked-draft status bar. In Edit mode we show either the
            currently-linked draft's name OR an invitation to link this
            document to an existing draft. */}
        {chat.mode === 'edit' && !linkPickerOpen && (
          linkedDraftName ? (
            <LinkedDraftStatusBar
              name={linkedDraftName}
              onRename={async (next) => {
                const id = getWordEditState().linkedDraftChatId;
                if (!id || next === linkedDraftName) return;
                const prev = linkedDraftName;
                // Optimistic local update.
                setLinkedDraftName(next);
                try {
                  await setWordEditState({ linkedDraftName: next });
                  await updateChatMeta({ chatId: id, title: next, isUserRenamed: true });
                } catch (err) {
                  // Revert on failure — best-effort.
                  setLinkedDraftName(prev);
                  await setWordEditState({ linkedDraftName: prev });
                  // eslint-disable-next-line no-console
                  console.warn('[TaskPane] draft rename failed', err);
                  setErrorBanner('Could not rename the draft. Please try again.');
                }
              }}
              onUnlink={handleUnlinkDraft}
            />
          ) : (
            <div className="flex items-center gap-2 px-3 py-1 bg-muted/60 border-b border-border text-[11px]">
              <Link2Icon />
              <span className="flex-1 min-w-0 truncate text-muted-foreground">
                Not linked to a draft.
              </span>
              <button
                type="button"
                onClick={() => setLinkPickerOpen(true)}
                className="text-primary hover:underline text-[10px] font-medium"
              >
                Link
              </button>
            </div>
          )
        )}

        {/* Phase F — ask-to-link banner. Shown when the DOCX carries a
            NyayAssist provenance stamp but isn't yet linked. */}
        {detectedProvenance && !linkPickerOpen && (
          <AskToLinkBanner
            draftName={detectedProvenance.name}
            busy={linkBusy}
            onLink={() => void confirmAutoLink()}
            onDismiss={() => void declineAutoLink()}
          />
        )}

        {/* One-time pin-add-in hint after first successful new-window open. */}
        {pinHintVisible && !linkPickerOpen && (
          <PinAddInTooltip
            onDismiss={() => {
              try {
                window.localStorage.setItem('nyayassist:pinHintShown', '1');
              } catch {
                /* storage disabled — dismissal won't persist across reloads */
              }
              setPinHintVisible(false);
            }}
          />
        )}
      </BannerStack>

      <ChatHistorySidebar
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelectThread={({ thread, messages }) => {
          // Research threads always load into the current pane.
          if (thread.chatType === 'research') {
            chat.loadThread(thread.chatId, messages, thread.chatType);
            chat.setMode('research');
            return;
          }

          // Draft (and legacy word_edit) threads: if the clicked thread IS the
          // currently-linked draft for this doc → just load the chat state.
          // Otherwise → ask to open in a new Word window (doc is different).
          const currentLinked = getWordEditState().linkedDraftChatId ?? null;
          if (currentLinked === thread.chatId || chat.currentThreadId === thread.chatId) {
            chat.loadThread(thread.chatId, messages, thread.chatType);
            chat.setMode('edit');
            return;
          }
          setSwitcherTarget({ thread, messages });
        }}
      />

      {/* Notification center — full-pane overlay (z-30, like history) + the
          transient toast host pinned bottom (z-50). */}
      <NotificationPanel onOpen={handleOpenNotification} />
      <ToastHost />

      {/* Overlay citation (narrow pane fallback) — covers chat area */}
      {overlayMode && (
        <CitationPanel
          variant="overlay"
          citation={activeCitation}
          onClose={handleCloseCitation}
          onInsertIntoDocument={handleInsertCitation}
        />
      )}

      {/* Draft-mode phase overlays (experimental, gated by FEATURES.draftMode) */}
      {FEATURES.draftMode && chat.mode === 'draft' && (
        <>
          {(draft.phase === 'template_loading' ||
            draft.phase === 'questions_loading' ||
            draft.phase === 'draft_loading') && (
            <DraftLoadingOverlay phase={draft.phase} onStop={draft.stop} />
          )}
          {draft.phase === 'template_review' && (
            <DraftTemplateOverlay
              template={draft.template}
              onChange={draft.updateTemplate}
              onNext={() => draft.confirmTemplate(user.sub ?? 'unknown')}
              onCancel={() => void draft.reset()}
            />
          )}
          {draft.phase === 'qna' && (
            <DraftQnaOverlay
              questions={draft.questions}
              onAnswer={draft.setAnswer}
              onSubmit={() => draft.submitAnswers(user.sub ?? 'unknown')}
              onStop={draft.stop}
            />
          )}
        </>
      )}

      {/* Main content: chat alone, or chat + citation split (web-UI style) */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {splitMode ? (
          <>
            <div className="flex-shrink-0 basis-[40%] min-w-[280px] max-w-[480px] h-full">
              {chatColumn}
            </div>
            <div className="flex-1 min-w-0">
              <CitationPanel
                variant="split"
                citation={activeCitation}
                onClose={handleCloseCitation}
                onInsertIntoDocument={handleInsertCitation}
              />
            </div>
          </>
        ) : (
          chatColumn
        )}
      </div>

      {/* Mode-switch fork confirmation */}
      {pendingForkText && chat.loadedChatType && (
        <ModeSwitchConfirmModal
          fromType={chat.loadedChatType}
          toMode={chat.mode}
          busy={forkBusy}
          onConfirm={confirmFork}
          onCancel={cancelFork}
        />
      )}

      {/* Link-to-draft picker */}
      <LinkDraftPicker
        open={linkPickerOpen}
        onClose={() => setLinkPickerOpen(false)}
        onPick={handleLinkDraft}
      />

      {/* Draft switcher — primary action opens in a new Word window via
          Word.Application.openDocument (WordApi 1.6) with a createDocument
          fallback. "Load here" replaces the current doc in place as a
          fallback for older Word builds or users who prefer one window. */}
      {switcherTarget && (
        <OpenDraftInNewWindowModal
          draftName={switcherTarget.thread.name ?? 'Untitled draft'}
          busy={switcherBusy}
          onCancel={() => setSwitcherTarget(null)}
          onOpenInNewWindow={() => void launchSwitcher({ mode: 'new-window' })}
          onLoadHere={() => void launchSwitcher({ mode: 'replace' })}
        />
      )}

    </div>
  );
}

/**
 * F7 — extracted linked-draft status bar with inline rename.
 *
 * Click the pencil to edit the name; Enter saves, Escape cancels.
 * Persists to both `Office.context.document.settings.linkedDraftName`
 * and to the server via PUT /api/v1/chat (with is_user_renamed=true).
 */
function LinkedDraftStatusBar({
  name,
  onRename,
  onUnlink,
}: {
  name: string;
  onRename: (next: string) => Promise<void>;
  onUnlink: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex items-center gap-2 px-3 py-1 bg-primary/5 border-b border-primary/20 text-[11px]">
      <Link2Icon />
      {editing ? (
        <span className="flex-1 min-w-0">
          <InlineTitleEditor
            initial={name}
            compact
            onSave={(next) => {
              setEditing(false);
              void onRename(next);
            }}
            onCancel={() => setEditing(false)}
          />
        </span>
      ) : (
        <>
          <span className="flex-1 min-w-0 truncate text-foreground/80">
            Linked to draft: <span className="font-medium">{name}</span>
          </span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-muted-foreground hover:text-foreground"
            title="Rename"
            aria-label="Rename draft"
          >
            <PencilIcon />
          </button>
          <button
            type="button"
            onClick={onUnlink}
            className="text-muted-foreground hover:text-foreground text-[10px] underline"
          >
            Unlink
          </button>
        </>
      )}
    </div>
  );
}

function PencilIcon(): JSX.Element {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function Link2Icon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="w-3 h-3 text-primary" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72" />
    </svg>
  );
}

/**
 * Compatibility matrix: does `mode` require a fork from `loadedType`?
 *
 *                mode=research   mode=edit   mode=draft
 *   research     no              YES         YES
 *   draft        YES             no          no
 *   word_edit*   YES             no          no    (* legacy — read-only)
 *
 * Unified model: every document-editing session lives in `chat_type='draft'`.
 * `word_edit` only appears for historical rows created before the migration;
 * we treat it like `draft` for fork-guard purposes so users can resume them.
 * New chats are always `draft`.
 */
function needsFork(
  loadedType: 'research' | 'word_edit' | 'draft' | null,
  mode: 'research' | 'edit' | 'draft',
): boolean {
  if (!loadedType) return false;
  switch (mode) {
    case 'research':
      return loadedType !== 'research';
    case 'edit':
      return loadedType !== 'draft' && loadedType !== 'word_edit';
    case 'draft':
      return loadedType !== 'draft' && loadedType !== 'word_edit';
  }
}

/** chat_type of a fresh new chat created in the given mode (unified model). */
function newChatTypeForMode(
  mode: 'research' | 'edit' | 'draft',
): 'research' | 'draft' {
  if (mode === 'research') return 'research';
  // edit + draft both land in the unified `draft` chat type.
  return 'draft';
}

function editStageLabel(stage: string): string {
  switch (stage) {
    case 'uploading':
      return 'Uploading document…';
    case 'sending':
      return 'Sending to NyayAssist…';
    case 'applying':
      return 'Applying edits to document…';
    default:
      return '';
  }
}

function extractFirstName(fullName: string | undefined): string {
  if (!fullName) return '';
  return fullName.split(/\s+/)[0] ?? fullName;
}

function EmptyState({ mode }: { mode: 'research' | 'edit' | 'draft' }): JSX.Element {
  if (mode === 'draft') return <DraftPromptHint />;
  const Icon = mode === 'research' ? MessageSquare : Pencil;
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div
        className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center mb-3',
          'bg-muted text-foreground/70',
        )}
      >
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-sm font-semibold mb-1">
        {mode === 'research' ? 'Research mode' : 'Edit mode'}
      </h3>
      <p className="text-xs text-muted-foreground max-w-xs">
        {mode === 'research'
          ? 'Ask a legal question — results stream in with citations you can insert.'
          : 'Describe how you want the document edited — changes appear as tracked changes in Word.'}
      </p>
    </div>
  );
}

function Header({
  userName,
  userPicture,
  onLogout,
  onNewChat,
  onToggleHistory,
  onToggleLinkDraft,
  showLinkDraft,
  editStage,
}: {
  userName: string;
  userPicture?: string;
  onLogout: () => void;
  onNewChat: () => void;
  onToggleHistory: () => void;
  /** Only visible when the current mode is Edit — opens the link-draft picker. */
  onToggleLinkDraft?: () => void;
  showLinkDraft?: boolean;
  editStage: string;
}): JSX.Element {
  const { unreadCount, togglePanel } = useNotifications();
  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
      <div className="flex items-center gap-2 min-w-0">
        {/* Brand: small NyayAssist logo + "NyayAssist" wordmark. Office
            already brackets this view with its own "NyayAssist" title bar
            (DisplayName from manifest), so the in-pane brand is just the
            wordmark — no redundant "for Word" suffix. Font inherits
            Source Sans Pro from body (globals.css:46) to match web. */}
        <img
          src="/logo_icon.png"
          alt="NyayAssist"
          className="w-7 h-7 rounded-md object-contain flex-shrink-0"
        />
        <div className="flex flex-col min-w-0 leading-tight">
          <span className="text-[15px] font-semibold tracking-tight text-foreground">
            NyayAssist
          </span>
          {userName && (
            <span className="text-[11px] text-muted-foreground truncate" title={userName}>
              {userName}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {userPicture && (
          <img
            src={userPicture}
            alt={userName}
            className="w-6 h-6 rounded-full mr-1 hidden sm:block"
            referrerPolicy="no-referrer"
          />
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={togglePanel}
          title="Notifications"
          aria-label="Notifications"
          className="relative"
        >
          <Bell className="w-3.5 h-3.5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] leading-[15px] text-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={onToggleHistory} title="Chat history">
          <History className="w-3.5 h-3.5" />
        </Button>
        {showLinkDraft && onToggleLinkDraft && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleLinkDraft}
            disabled={editStage !== 'idle'}
            title="Link this document to an existing draft"
          >
            <Link2Icon />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onNewChat}
          disabled={editStage !== 'idle'}
          title="New chat"
        >
          <SquarePen className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onLogout} title="Sign out">
          <LogOut className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
